import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { CustomModal, Box, MultiSelectFilterPicker, Page, PageContentHeader, PageEmptyState, PageErrorState, Searchbox, DataTable, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { CreateProposalButton } from '~/components/CreateProposalButton/CreateProposalButton';
import LoadingIndicator from '~/components/LoadingIndicator';
import { useRestfulWrapper } from '~/hooks/useRestfulWrapper';
import { useServiceTemplateApi } from '~/hooks/useServiceTemplateApi';
import { useTableColumnMenu } from '~/hooks/useTableColumnMenu';
import { useLocaleKeys } from '~/i18n';
import { modalSize } from '~/infra/modals';
import { handlePromise } from '~/infra/utils';
import NoDataIcon from '~/integrations/assets/NoDataIcon';
import { PracticeManagement } from '~/types/api.model';
import { getPMDetails } from '../pm.utils';
import { useSerialTaskQueue } from './hooks/useSerialTaskQueue';
import styles from './PMMapServicesModal.module.scss';
import { MappedStatus, filterTemplates, serviceColumns, toServiceRows } from './PMMapServicesModal.table.utils';
export const GetPMMapServicesModalTID = (parentTestId)=>getTestIds(parentTestId);
const mappedStatusFilterOptions = [
    {
        value: MappedStatus.Mapped,
        label: 'Mapped'
    },
    {
        value: MappedStatus.Unmapped,
        label: 'Unmapped'
    }
];
function PMMapServicesTable(param) {
    let { pmName, templates: initialTemplates, items, getServiceTemplateIntegratedIds, linkServiceTemplate, refresh } = param;
    const { t: practiceManagementTexts } = useLocaleKeys('integrations.practiceManagement');
    const { t: commonTexts } = useLocaleKeys('common');
    const [integratedIdsByTemplate, setIntegratedIdsByTemplate] = useState({});
    const [search, setSearch] = useState('');
    const [mappedStatusFilter, setMappedStatusFilter] = useState([]);
    const { showErrorToast } = useOverlayContext();
    const [templates, setTemplates] = useState(initialTemplates);
    const { PMMapServicesModalTID } = useMemo(()=>GetPMMapServicesModalTID(), []);
    const filteredTemplates = useMemo(()=>filterTemplates(templates, search, mappedStatusFilter, getServiceTemplateIntegratedIds), [
        templates,
        search,
        mappedStatusFilter,
        getServiceTemplateIntegratedIds
    ]);
    const integratedIdsFor = (template)=>{
        var _integratedIdsByTemplate_template_id;
        return (_integratedIdsByTemplate_template_id = integratedIdsByTemplate[template.id]) !== null && _integratedIdsByTemplate_template_id !== void 0 ? _integratedIdsByTemplate_template_id : getServiceTemplateIntegratedIds(template);
    };
    const serviceRows = toServiceRows(filteredTemplates, integratedIdsFor);
    const templatesById = useMemo(()=>new Map(filteredTemplates.map((template)=>[
                template.id,
                template
            ])), [
        filteredTemplates
    ]);
    const columnMenu = useTableColumnMenu('pm_map_services');
    const enqueueForTemplate = useSerialTaskQueue();
    const changeMapping = (template, ids)=>{
        setIntegratedIdsByTemplate((current)=>_object_spread_props(_object_spread({}, current), {
                [template.id]: ids
            }));
        return enqueueForTemplate(template.id, ()=>mapService(template, ids));
    };
    const commitMapping = (param)=>{
        let { row, value } = param;
        const template = templatesById.get(row.id);
        if (template != null) void changeMapping(template, Array.isArray(value) ? [
            ...value
        ] : []);
    };
    const [mainColumn, ...columns] = serviceColumns({
        pmName,
        items,
        emptySearchText: practiceManagementTexts.mapServicesModal.noMatchingTemplates()
    });
    async function mapService(template, itemIds) {
        const result = await handlePromise(linkServiceTemplate(template.id, itemIds));
        if (!result.success) {
            await refresh();
            showErrorToast("Failed to map service ".concat(template.name), 'map-service-error-toast');
        }
        setTemplates((current)=>current.map((t)=>t.id === template.id ? withIntegratedIds(t, itemIds) : t));
    }
    function withIntegratedIds(template, itemIds) {
        const integrations = template.integrations;
        const setIntegratedIds = {
            [PracticeManagement.Keeper]: ()=>integrations.setKeeperIntegratedIds(itemIds),
            [PracticeManagement.Karbon]: ()=>integrations.setKarbonIntegratedIds(itemIds),
            [PracticeManagement.ClientHub]: ()=>integrations.setClientHubIntegratedIds(itemIds),
            [PracticeManagement.FinancialCents]: ()=>integrations.setFinancialCentsIntegratedIds(itemIds)
        };
        return _object_spread_props(_object_spread({}, template), {
            integrations: setIntegratedIds[pmName]()
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(PageContentHeader, {
                children: /*#__PURE__*/ _jsxs(Box, {
                    className: styles.filtersRow,
                    children: [
                        /*#__PURE__*/ _jsx(Searchbox, {
                            bi: {
                                name: 'pm_services_search'
                            },
                            testId: PMMapServicesModalTID.Search(),
                            placeholder: commonTexts.searchPlaceholder(),
                            value: search,
                            onChange: (e)=>setSearch(e.target.value),
                            onClear: ()=>setSearch('')
                        }),
                        /*#__PURE__*/ _jsx(MultiSelectFilterPicker, {
                            bi: {
                                name: 'pm_mapped_status_filter'
                            },
                            value: mappedStatusFilter,
                            options: mappedStatusFilterOptions,
                            label: practiceManagementTexts.mappedStatus(),
                            onChange: setMappedStatusFilter,
                            testId: PMMapServicesModalTID.MappedStatusFilter()
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(DataTable, {
                mainColumn: mainColumn,
                allowColumnResize: true,
                allowColumnReorder: true,
                reorderBi: {
                    name: 'pm_map_services_reorder_columns'
                },
                allowColumnHide: true,
                columnMenu: columnMenu,
                columns: columns,
                items: serviceRows,
                getRowId: (row)=>row.id,
                mobile: {
                    fields: [
                        {
                            name: 'service'
                        },
                        {
                            name: 'mapping'
                        }
                    ]
                },
                onCellChange: commitMapping,
                testId: PMMapServicesModalTID.Table()
            })
        ]
    });
}
export function PMMapServicesModal(props) {
    const { t: practiceManagementTexts } = useLocaleKeys('integrations.practiceManagement');
    const { t: errorTexts } = useLocaleKeys('errors');
    const { pmName, dismissModal, getItems } = props;
    const serviceTemplateApi = useServiceTemplateApi();
    const { renderRestfulState } = useRestfulWrapper({
        fetch
    });
    const { PMMapServicesModalTID } = useMemo(()=>GetPMMapServicesModalTID(), []);
    async function fetch() {
        const [templates, items] = await Promise.all([
            serviceTemplateApi.getForMappingToPracticeManagementIntegration(),
            getItems()
        ]);
        return {
            templates,
            items
        };
    }
    return /*#__PURE__*/ _jsx(CustomModal, _object_spread_props(_object_spread({
        bi: {
            closeButton: {
                name: 'pm_map_services_modal_close'
            }
        },
        testId: PMMapServicesModalTID.Root()
    }, modalSize.large), {
        onClose: dismissModal,
        children: /*#__PURE__*/ _jsx(Page, {
            height: "100%",
            header: practiceManagementTexts.mapServicesHeader({
                pmName: getPMDetails(pmName).name
            }),
            skin: "secondary",
            children: renderRestfulState({
                renderChild: (param)=>{
                    let { data, refresh } = param;
                    return /*#__PURE__*/ _jsx(Box, {
                        testId: PMMapServicesModalTID.Content(),
                        children: data.templates.length > 0 ? /*#__PURE__*/ _jsx(PMMapServicesTable, _object_spread_props(_object_spread({}, props, data), {
                            refresh: refresh
                        })) : /*#__PURE__*/ _jsx(PageEmptyState, {
                            testId: PMMapServicesModalTID.NoServices(),
                            illustration: /*#__PURE__*/ _jsx(NoDataIcon, {}),
                            message: practiceManagementTexts.mapServicesEmptyMessage({
                                pmName: getPMDetails(pmName).name
                            }),
                            actions: /*#__PURE__*/ _jsx(CreateProposalButton, {
                                text: practiceManagementTexts.createAgreement()
                            })
                        })
                    });
                },
                renderLoader: ()=>/*#__PURE__*/ _jsx(LoadingIndicator, {}),
                renderError: (param)=>{
                    let { refresh } = param;
                    return /*#__PURE__*/ _jsx(PageErrorState, {
                        testId: PMMapServicesModalTID.FetchError(),
                        message: errorTexts.integration.dataRetrieval(),
                        onRefresh: refresh,
                        skin: "secondary"
                    });
                }
            })
        })
    }));
}
